/**
 * Disabled stations in the local system (M5 spec §14.4, §15.5, §18 flow 5; plan C15).
 *
 * A station a war disabled (`StationView.disabledBy`, the disabling faction) goes dark and closes: its lights and
 * emissive are scaled by `disabled.lightScale` (charter-conflict.json), its market and services close (the strategic
 * market returns no rows or quotes and refuses trades, `strategic-link/market.ts`), and docking stays allowed for
 * shelter (the station screen shows only the banner and Launch; C18 draws it from `stationServicesClosed`).
 *
 * - **Lights:** `applyDisabledLooks` runs on every station sync (`strategic-link/stations.ts`) and sends only the
 *   changes through the session's `setStationLights(systemId, siteId, scale)` hook (1 = normal). The Reach
 *   session keeps the scale per system and the structures feature hides the station's LIGHT_ sprites below half
 *   scale (placeholder: the shared hull materials keep their emissive). A host without the hook logs one line.
 * - **Closed state:** `isStationClosed(stationId)` answers from the latest sync of the station's system, for the
 *   station screen and the HUD.
 *
 * Runs on manifests and system views (rare events); allocates freely.
 */
import { z } from 'zod'
import conflictJson from '../data/charter-conflict.json'
import { logger } from '../core/logger'
import type { CorpFactionId, StationView } from '../strategic/types'

const disabledConfigSchema = z
  .object({
    /** The light and emissive scale of a disabled station (0 = dark, 1 = normal). */
    lightScale: z.number().min(0).max(1),
  })
  .strict()

/** Disabled-station tuning (charter-conflict.json `disabled`). */
export const DISABLED_CONFIG = disabledConfigSchema.parse(conflictJson.disabled)

/** The light scale of a station that is not disabled. */
export const NORMAL_LIGHT_SCALE = 1

/** Whether a war disabled the station. */
export function isStationDisabled(view: Pick<StationView, 'disabledBy'>): boolean {
  return view.disabledBy !== null
}

/** The faction that disabled the station, or null. */
export function disablerOf(view: Pick<StationView, 'disabledBy'>): CorpFactionId | null {
  return view.disabledBy
}

/**
 * Whether the station's market and services are closed (spec §14.4): true while it is disabled. Docking is still
 * allowed (shelter); the station screen then shows only the banner and Launch.
 */
export function stationServicesClosed(view: Pick<StationView, 'disabledBy'>): boolean {
  return isStationDisabled(view)
}

/** The ids of the disabled stations in a station list, in list order. */
export function disabledStationIds(stations: readonly Pick<StationView, 'id' | 'disabledBy'>[]): string[] {
  const out: string[] = []
  for (const v of stations) if (isStationDisabled(v)) out.push(v.id)
  return out
}

/** The light scale a station should show. */
export function stationLightScale(view: Pick<StationView, 'disabledBy'>, lightScale: number = DISABLED_CONFIG.lightScale): number {
  return isStationDisabled(view) ? lightScale : NORMAL_LIGHT_SCALE
}

/** The optional session hook that dims a station's lights and emissive (not on M3's session yet). */
export interface StationLightsHost {
  setStationLights(systemId: string, siteId: string, scale: number): void
}

/** Whether `host` has the lights hook. */
export function isStationLightsHost(host: object): host is StationLightsHost {
  return typeof (host as Partial<StationLightsHost>).setStationLights === 'function'
}

/** Per system: the disabled station ids of its latest sync (for `isStationClosed`). */
const closedBySystem = new Map<string, ReadonlySet<string>>()
/** Per host: the light scale each `systemId/siteId` was last given (only changes are sent). */
const appliedScales = new WeakMap<object, Map<string, number>>()
/** Hosts already told (once) that they lack the lights hook. */
const warnedHosts = new WeakSet<object>()

/** Whether the station (strategic id) is disabled in the latest sync of its system. */
export function isStationClosed(stationId: string): boolean {
  for (const set of closedBySystem.values()) if (set.has(stationId)) return true
  return false
}

/** Forgets every system's closed stations (a career ends or a test resets). */
export function clearClosedStations(): void {
  closedBySystem.clear()
}

/**
 * Applies one system's disabled state: records its closed stations and sends each station's light scale that
 * changed since the last sync to the host (a station never dimmed is not touched). Returns how many stations the
 * host was told about; 0 (and one log line per host) when the host has no `setStationLights`.
 */
export function applyDisabledLooks(
  host: object,
  systemId: string,
  stations: readonly Pick<StationView, 'id' | 'disabledBy'>[],
  lightScale: number = DISABLED_CONFIG.lightScale,
): number {
  const closed = disabledStationIds(stations)
  if (closed.length > 0) closedBySystem.set(systemId, new Set(closed))
  else closedBySystem.delete(systemId)
  if (!isStationLightsHost(host)) {
    if (closed.length > 0 && !warnedHosts.has(host)) {
      warnedHosts.add(host)
      logger.warn('charter: the Reach session has no setStationLights; disabled stations keep their lights')
    }
    return 0
  }
  let applied = appliedScales.get(host)
  if (applied === undefined) {
    applied = new Map()
    appliedScales.set(host, applied)
  }
  let sent = 0
  for (const v of stations) {
    const key = `${systemId}/${v.id}`
    const scale = stationLightScale(v, lightScale)
    const before = applied.get(key)
    if (before === undefined ? scale === NORMAL_LIGHT_SCALE : before === scale) continue
    applied.set(key, scale)
    try {
      host.setStationLights(systemId, v.id, scale)
      sent++
    } catch (err: unknown) {
      logger.error(`charter: dimming the lights of ${v.id} failed`, err)
    }
  }
  return sent
}
